import { Button, buttonVariants } from "@repo/ui/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@repo/ui/components/ui/form";
import { addDays, startOfDay } from "date-fns";
import { cs, enUS, pl } from "date-fns/locale";
import { useLocale, useTranslations } from "next-intl";
import {
  forwardRef,
  type Ref,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import type { ClassNames } from "react-day-picker";
import type { ControllerRenderProps, UseFormReturn } from "react-hook-form";
import { CalendarNew } from "@/components/ui/calendar-new";
import { cn } from "@/lib/utils";

type Props = {
  form: UseFormReturn<any, any, any>;
  fieldName: string;
  fieldNameDaysToSelect: string;
  daysToSelect?: number;
  disabledDays?: Date[];
  disableSaturdays?: boolean;
  disableSundays?: boolean;
  firstPossibleOrderDay?: Date;
  lastPossibleOrderDay?: Date;
  isDisabledDate?: (date: Date) => boolean;
  showMessage?: boolean;
  calendarClasses?: Partial<ClassNames>;
  showClearButton?: boolean;
  showOutsideDays?: boolean;
  fixedWeeks?: boolean;
  disableAutoNextDays?: boolean;
  firstPossibleChangeDay?: Date;
  // When true, if the current selection count reached daysToSelect, prevent selecting new (unselected) dates
  // while still allowing interaction with already selected ones (to deselect).
  blockSelectingNewDaysWhenFull?: boolean;
};

export type CalendarWithMonthsHandle = {
  handleClear: () => void;
};

const CalendarWithMonths = ({
  months,
  field,
  vertical = false,
  locale = "pl",
  handleSelection,
  isDisabledDate,
  customKey,
  fixedWeeks,
  defaultMonth,
  calendarClasses,
  showOutsideDays = true,
}: {
  months: number;
  field: ControllerRenderProps<any, string>;
  vertical?: boolean;
  locale?: string;
  handleSelection: (dates?: Date[]) => void;
  isDisabledDate: (date: Date) => boolean;
  customKey: string;
  fixedWeeks?: boolean;
  defaultMonth?: Date;
  calendarClasses?: Partial<ClassNames>;
  showOutsideDays?: boolean;
}) => {
  const getCalendarLocale = () => {
    switch (locale) {
      case "pl":
        return pl;
      case "en":
        return enUS;
      case "cz":
        return cs;
      default:
        return pl;
    }
  };

  return (
    <FormControl>
      <CalendarNew
        // mode 'multiple' is hardcoded because we want to allow selecting multiple dates
        // there is only one use of calendar form in our application which is date selection
        // for orders - it will always be multiple
        defaultMonth={defaultMonth}
        disabled={isDisabledDate}
        fixedWeeks={fixedWeeks}
        key={customKey}
        locale={getCalendarLocale()}
        mode="multiple"
        numberOfMonths={months}
        onSelect={handleSelection}
        selected={field.value}
        showOutsideDays={showOutsideDays}
        vertical={vertical}
      />
    </FormControl>
  );
};

export const FormDatePicker = forwardRef(
  (
    {
      form,
      fieldName,
      fieldNameDaysToSelect,
      isDisabledDate,
      daysToSelect,
      disableSaturdays,
      disableSundays,
      firstPossibleOrderDay,
      lastPossibleOrderDay,
      disabledDays,
      showMessage = true,
      showClearButton = true,
      calendarClasses,
      showOutsideDays = true,
      fixedWeeks,
      disableAutoNextDays = false,
      firstPossibleChangeDay,
      blockSelectingNewDaysWhenFull,
    }: Props,
    ref: Ref<CalendarWithMonthsHandle>
  ) => {
    const t = useTranslations();
    const [selectedDates, setSelectedDates] = useState<Date[]>(
      form.getValues(fieldName) || []
    );

    const [touched, setTouched] = useState(false);

    const locale = useLocale();

    useImperativeHandle(ref, () => ({
      handleClear,
    }));

    // if selectedDates change, update the form value
    useEffect(() => {
      form.setValue(fieldName, selectedDates, {
        shouldDirty: true,
        shouldTouch: true,
      });
      if (touched) form.trigger(fieldName);
    }, [selectedDates, daysToSelect]);

    // If user changes the disabled days configuration, remove the disabled dates from the selected dates
    useEffect(() => {
      const filtered = filterSelectedDates(selectedDates);
      setSelectedDates(filtered);
      if (selectedDates.length > 0 && filtered.length > 0)
        form.setValue(fieldNameDaysToSelect, filtered.length);
    }, [disableSaturdays, disableSundays]);

    // If user changes the daysToSelect
    useEffect(() => {
      if (!daysToSelect) return;
      if (selectedDates.length > daysToSelect) {
        const count = selectedDates.length - daysToSelect;
        setSelectedDates(
          selectedDates.filter((date) =>
            findFirstDays(selectedDates, count).includes(date)
          )
        );
      }
      if (
        selectedDates.length &&
        selectedDates.length < daysToSelect &&
        !disableAutoNextDays
      ) {
        const addedDates = findNextAvailableDatesArray(
          findLastDay(selectedDates),
          daysToSelect - selectedDates.length
        );
        if (addedDates.length) {
          setSelectedDates(
            [...selectedDates, ...addedDates].sort(
              (a, b) => a.getTime() - b.getTime()
            )
          );
        }
      }
    }, [selectedDates, daysToSelect]);

    const filterSelectedDates = (dates: Date[]) => {
      if (firstPossibleChangeDay) {
        return dates.filter((date) => {
          return (
            !isDateDisabledChecker(date) ||
            startOfDay(date) <= startOfDay(firstPossibleChangeDay)
          );
        });
      }

      return dates.filter((date) => !isDateDisabledChecker(date));
    };

    // check if the date is disabled
    const isDateDisabledChecker = (day: Date) => {
      if (isDisabledDate?.(day)) return true;

      if (
        blockSelectingNewDaysWhenFull &&
        daysToSelect &&
        selectedDates.length >= daysToSelect
      ) {
        const isAlreadySelected = selectedDates.some(
          (d) => d.toDateString() === day.toDateString()
        );
        if (!isAlreadySelected) return true;
      }

      const isWeekend =
        (disableSaturdays && day.getDay() === 6) ||
        (disableSundays && day.getDay() === 0);
      const isExplicitlyDisabled = Boolean(
        disabledDays?.find((date) => date.toDateString() === day.toDateString())
      );
      const isBeforeFirstOrderDay = Boolean(
        firstPossibleOrderDay &&
          startOfDay(day) < startOfDay(firstPossibleOrderDay)
      );
      const isBeforeFirstChangeDay = Boolean(
        firstPossibleChangeDay &&
          startOfDay(day) < startOfDay(firstPossibleChangeDay)
      );
      const isAfterLastOrderDay = Boolean(
        lastPossibleOrderDay &&
          startOfDay(day) > startOfDay(lastPossibleOrderDay)
      );

      return (
        isWeekend ||
        isExplicitlyDisabled ||
        isBeforeFirstOrderDay ||
        isBeforeFirstChangeDay ||
        isAfterLastOrderDay
      );
    };

    // find the next available date
    const findNextAvailableDate = (date: Date): Date | undefined => {
      const nextDay = addDays(date, 1);
      if (
        lastPossibleOrderDay &&
        startOfDay(nextDay) > startOfDay(lastPossibleOrderDay)
      )
        return;
      if (!isDateDisabledChecker(nextDay)) return nextDay;
      return findNextAvailableDate(nextDay);
    };

    const findNextAvailableDatesArray = (date: Date, count: number) => {
      const nextDays: Date[] = [];
      let nextDay = date;
      for (let i = 0; i < count; i++) {
        const next = findNextAvailableDate(nextDay);
        if (!next) break;
        nextDay = next;
        nextDays.push(nextDay);
      }
      return nextDays;
    };

    // find the last selected date - selectedDays can be unordered
    const findLastDay = (dates: Date[]) => {
      const lastDate = dates.reduce((a, b) => (a > b ? a : b));
      return lastDate;
    };

    // find the last [count] days from the selected dates
    const findFirstDays = (dates: Date[], count: number) => {
      const lastDates = dates
        .sort((a, b) => a.getTime() - b.getTime())
        .slice(0, dates.length - count);
      return lastDates;
    };

    // handle the first selection - user didn't select any date yet and it's the first one
    const firstSelection = (date: Date) => {
      if (!daysToSelect) daysToSelect = 1;

      // create an array of [daysToSelect] dates from the first selected date

      let addedDates = Array.from({ length: daysToSelect - 1 }, (_, i) =>
        addDays(date, i + 1)
      );

      const disabled = addedDates.filter((date) => isDateDisabledChecker(date));

      // remove the disabled dates from the array
      addedDates = addedDates.filter((date) => !disabled.includes(date));

      // add the next available dates to the array
      for (const _ of disabled) {
        const nextDate = findNextAvailableDate(
          findLastDay([date, ...addedDates])
        );
        if (!nextDate) break;
        addedDates.push(nextDate);
      }

      setSelectedDates([date, ...addedDates]);
    };

    // handle the selection of the dates
    const handleSelection = (dates?: Date[]) => {
      if (!dates) return;
      setTouched(true);

      // first selection
      if (selectedDates.length === 0 && dates.length >= 0) {
        firstSelection(dates[0] as Date);
      } else {
        setSelectedDates(
          dates?.sort((a, b) => a.getTime() - b.getTime()) || []
        );
        if (dates.length !== daysToSelect) {
          if (dates.length === 0) {
            form.resetField(fieldNameDaysToSelect);
          } else {
            form.setValue(fieldNameDaysToSelect, dates.length);
          }
          form.trigger(fieldNameDaysToSelect);
        }
      }
    };

    // clear all of the selected dates
    const handleClear = () => {
      setSelectedDates([]);
    };

    return (
      <FormField
        control={form.control}
        name={fieldName}
        render={({ field }) => (
          <>
            {showClearButton && (
              <div className="flex items-center gap-4 md:flex-row mb-6">
                <Button
                  className="m-0 p-0 text-base"
                  data-pw="clear-days"
                  onClick={handleClear}
                  size="sm"
                  type="button"
                  variant="link"
                >
                  {t("cart.calendar.clear")}
                </Button>
              </div>
            )}
            {showMessage && <FormMessage t={t} />}
            <FormItem className="flex h-full w-full items-center gap-4 rounded-lg bg-background p-2 pt-3 md:p-4">
              <div className="hidden w-full lg:grid">
                <CalendarWithMonths
                  calendarClasses={calendarClasses}
                  customKey="calendar-desktop"
                  defaultMonth={firstPossibleOrderDay}
                  field={field}
                  fixedWeeks={fixedWeeks}
                  handleSelection={handleSelection}
                  isDisabledDate={isDateDisabledChecker}
                  locale={locale}
                  months={2}
                  showOutsideDays={showOutsideDays}
                />
              </div>
              <div className="grid w-full lg:hidden">
                <CalendarWithMonths
                  calendarClasses={calendarClasses}
                  customKey="calendar-mobile"
                  defaultMonth={firstPossibleOrderDay}
                  field={field}
                  fixedWeeks={fixedWeeks}
                  handleSelection={handleSelection}
                  isDisabledDate={isDateDisabledChecker}
                  locale={locale}
                  months={1}
                  showOutsideDays={showOutsideDays}
                  vertical
                />
              </div>
            </FormItem>
          </>
        )}
      />
    );
  }
);

FormDatePicker.displayName = "FormDatePicker";
